import * as React from 'react';
import { useSelector } from 'react-redux';
import { trackClickShowPhone } from 'shared/components/author/tracking';
import { useApplicationContext } from 'shared/containers/ApplicationContext';
import { PhonesOpenedInfoContainer } from 'shared/containers/BottomAuthorBlockNew/PhonesOpenedInfoContainer/PhonesOpenedInfoContainer';
import { ElectronicTradingContactButtonContainer } from 'shared/containers/ElectronicTradingContactButtonContainer';
import { useChats } from 'shared/hooks/useChats';
import { useDynamicPhoneFlow } from 'shared/hooks/useDynamicPhoneFlow';
import { useOfferDynamicCalltrackingEnabled } from 'shared/hooks/useOfferDynamicCalltrackingEnabled';
import { getRosreestrInfo } from 'shared/selectors/offerData/getRosreestrInfo';
import { selectKpId } from 'shared/selectors/offerData/kp';
import { selectAgent } from 'shared/selectors/selectAgent';
import { EActionType } from 'shared/types/dailyrent';
import { getFormattedPhoneNumber } from 'shared/utils/formatters';
import { SimpleBotDetector } from 'shared/utils/simpleBotDetector';
import { trackShowStatNumber } from 'shared/utils/tracking/trackShowStatNumber';
import { v4 as uuid } from 'uuid';

import { SubscriptionAuthorActions } from '../../../components/subscriptionBrand';
import { useIsElectronicTradingOffer } from '../../../hooks/useIsElectronicTradingOffer';
import { selectIsOfferOwnedByUser, offerSelector, selectABGroup, selectUser } from '../../../selectors';
import { selectIsChatsEnabled, selectIsOfferChatExists } from '../../../selectors/offerChat';
import { selectDynamicPhones } from '../../../selectors/offerData/getActualPhones';
import { getPhones } from '../../../selectors/offerData/getPhones';
import { offerPreparedPhonesSelector } from '../../../selectors/offerData/offer/offerPreparedPhonesSelector';

export const SubscriptionAuthorActionsContainer = () => {
  const isOfferChatExists = useSelector(selectIsOfferChatExists);
  const isOfferOwnedByUser = useSelector(selectIsOfferOwnedByUser);
  const dynamicPhones = useSelector(selectDynamicPhones);
  const staticPreparedPhones = useSelector(offerPreparedPhonesSelector);
  const staticPhones = useSelector(getPhones);
  const isChatsEnabled = useSelector(selectIsChatsEnabled);
  const offer = useSelector(offerSelector);
  const agent = useSelector(selectAgent);
  const abGroup = useSelector(selectABGroup);
  const user = useSelector(selectUser);
  const kpId = useSelector(selectKpId);
  const rosreestrInfo = useSelector(getRosreestrInfo);
  const { telemetry } = useApplicationContext();
  const { handleOpenChat } = useChats();

  const actionType = offer?.dailyrent?.actionType;
  const onlyChatButtonActionType =
    !!actionType && [EActionType.OtaBooking, EActionType.OtaCheckDates, EActionType.Chat].includes(actionType);

  const isElectronicTradingOffer = useIsElectronicTradingOffer();
  const isOfferDynamicCalltrackingEnabled = useOfferDynamicCalltrackingEnabled();
  const { requestPhone } = useDynamicPhoneFlow();

  const [isPhonesVisible, setIsPhonesVisible] = React.useState(false);
  const [isPhonesLoading, setPhonesLoading] = React.useState(false);
  const [botDetector] = React.useState<SimpleBotDetector>(new SimpleBotDetector());

  const isMessageButtonVisible = isChatsEnabled && (isOfferChatExists || !isOfferOwnedByUser);

  React.useEffect(() => {
    botDetector.init();

    return () => botDetector.destroy();
  }, [botDetector]);

  // Подменные номера динКТ (SEL-998) приоритетнее статических; без них — прежний селектор,
  // чтобы вне эксперимента блок вёл себя байт-в-байт как раньше
  const phones = React.useMemo(
    () => (dynamicPhones ? dynamicPhones.map(getFormattedPhoneNumber).filter(Boolean) : staticPreparedPhones),
    [dynamicPhones, staticPreparedPhones],
  );

  const handleShowPhonesClick = React.useCallback(async () => {
    const clickId = isOfferDynamicCalltrackingEnabled ? uuid() : undefined;

    trackClickShowPhone({
      offer,
      agent,
      abGroup,
      user,
      kpId,
      isBot: !botDetector.isHuman(),
      rosreestrInfo,
      clickId,
    });

    if (clickId) {
      setPhonesLoading(true);
      const hasPhoneToShow = await requestPhone({
        offerId: offer.id,
        clickId,
        hasStaticPhones: staticPhones.length > 0,
        onDegradation: () => trackShowStatNumber({ offer, agent, user, clickId }),
      });
      setPhonesLoading(false);

      if (!hasPhoneToShow) {
        return;
      }
    }

    setIsPhonesVisible(true);
  }, [
    offer,
    agent,
    abGroup,
    user,
    kpId,
    botDetector,
    rosreestrInfo,
    isOfferDynamicCalltrackingEnabled,
    requestPhone,
    staticPhones,
    setIsPhonesVisible,
  ]);

  const handleSendMessageClick = React.useCallback(() => {
    telemetry.increment('offer_card.send_message_click.subscription_author');
    handleOpenChat();
  }, [telemetry, handleOpenChat]);

  return (
    <SubscriptionAuthorActions
      isPhonesVisible={isPhonesVisible}
      isPhonesLoading={isPhonesLoading}
      phones={phones}
      isMessageButtonVisible={isMessageButtonVisible}
      messageButtonText="Написать сообщение"
      handleShowPhonesClick={handleShowPhonesClick}
      handleSendMessageClick={handleSendMessageClick}
      phonesOpenedInfo={<PhonesOpenedInfoContainer />}
      isElectronicTradingOffer={isElectronicTradingOffer}
      onlyChatButtonActionType={onlyChatButtonActionType}
      electronicTradingContactButton={
        <ElectronicTradingContactButtonContainer
          buttonSize="XS"
          dataTestId="electronic-trading-button-subscription-actions"
        />
      }
    />
  );
};
